import * as React from "react";
import { useMemo, useRef } from "react";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import {
  Dropdowns,
  InputContext,
  PossibleColumnProps,
  RowType,
} from "../types";
import { InputNumber } from "primereact/inputnumber";
import CustomChipsDisplay from "./CustomChipsDisplay";
import DeliveryStatusTag from "./DeliveryStatusTag";
import { DropdownOpenAddNew } from "./DropdownOpenAddNew.tsx";

type CellEditorProps<TRow extends RowType> = {
  column: PossibleColumnProps<TRow>;
  value: unknown;
  rowData: Partial<TRow>;
  dropdownConfigs: Partial<Dropdowns>;
  onChange: (newValue: unknown) => void;
  closeEditor?: () => void;
  inputContext?: InputContext;
};

function isValidDate(value: unknown): value is Date {
  return value instanceof Date && !isNaN(value.getTime());
}

// ToDo: Possibly we could use form and on submit instead of this, would maybe be mobile friendlier as well
function handleEditorEnterKey(
  e: React.KeyboardEvent<HTMLInputElement | HTMLDivElement>,
  onChange: (value: string) => void,
  closeEditor?: () => void,
) {
  if (e.key === "Enter") {
    onChange((e.currentTarget as HTMLInputElement).value);
    closeEditor?.();
  }
  e.stopPropagation();
}

export default function CellEditor<TRow extends RowType>({
  column,
  value,
  rowData,
  dropdownConfigs: originalDropdownConfigs,
  onChange,
  closeEditor,
  inputContext = "deliveryTable", // ToDo: why is this default?
}: CellEditorProps<TRow>) {
  const dropdownFilterRef = useRef<string>("");

  const { purchaseContract, salesContract } = useMemo(() => {
    return {
      purchaseContract: (rowData as Record<string, unknown>)[
        "purchaseContract"
      ],
      salesContract: (rowData as Record<string, unknown>)["salesContract"],
    };
  }, [rowData]);

  const selectedDriver = useMemo(() => {
    return (
      originalDropdownConfigs.drivers?.find(
        (driver) =>
          // ToDo: actually require TRow to have those fields to use driver based stuff
          ("driverId" in rowData && driver.data.id === rowData.driverId) ||
          ("driver" in rowData &&
            typeof rowData.driver === "number" &&
            driver.data.id === rowData.driver),
      ) ?? null
    );
  }, [originalDropdownConfigs, rowData]);

  const selectedSalesContract = useMemo(() => {
    const salesContract = (rowData as Record<string, unknown>)["salesContract"];
    return typeof salesContract === "string"
      ? originalDropdownConfigs.salesContracts?.find(
          (contract) => contract.label === salesContract,
        )
      : null;
  }, [originalDropdownConfigs, rowData]);

  const selectedPurchaseContract = useMemo(() => {
    const purchaseContract = (rowData as Record<string, unknown>)[
      "purchaseContract"
    ];
    return typeof purchaseContract === "string"
      ? (originalDropdownConfigs.purchaseContracts?.find(
          (contract) => contract.label === purchaseContract,
        ) ?? null)
      : null;
  }, [originalDropdownConfigs, rowData]);

  const selectedProduct = useMemo(() => {
    const product = (rowData as Record<string, unknown>)["product"];
    return typeof product === "string"
      ? (originalDropdownConfigs.products?.find((p) => p.label === product) ??
          null)
      : null;
  }, [originalDropdownConfigs, rowData]);

  const dropdownConfigs = useMemo(() => {
    return {
      ...originalDropdownConfigs,
      trucks:
        selectedDriver?.data.vehicles
          .filter((vehicle) => vehicle.type === "truck")
          .map((vehicle) => ({ label: vehicle.number, value: vehicle.id })) ??
        [],
      trailers:
        selectedDriver?.data.vehicles
          .filter((vehicle) => vehicle.type !== "truck")
          .map((vehicle) => ({ label: vehicle.number, value: vehicle.id })) ??
        [],
      salesContracts:
        selectedPurchaseContract || selectedProduct
          ? (originalDropdownConfigs.salesContracts?.filter(
              (contract) =>
                contract.data.product.id ===
                (selectedPurchaseContract?.data.product.id ||
                  selectedProduct?.value),
            ) ?? [])
          : (originalDropdownConfigs.salesContracts ?? []),
      purchaseContracts:
        selectedSalesContract || selectedProduct
          ? (originalDropdownConfigs.purchaseContracts?.filter(
              (contract) =>
                contract.data.product.id ===
                (selectedSalesContract?.data.product.id ||
                  selectedProduct?.value),
            ) ?? [])
          : (originalDropdownConfigs.purchaseContracts ?? []),
    };
  }, [
    originalDropdownConfigs,
    selectedDriver,
    selectedSalesContract,
    selectedPurchaseContract,
    selectedProduct,
  ]);

  const selectItems = React.useMemo(
    () =>
      (dropdownConfigs &&
        column.optionsKey &&
        dropdownConfigs[column.optionsKey]) ??
      [],
    [column.optionsKey, dropdownConfigs],
  );

  const resolvedInputType = React.useMemo(
    () => column.contextInputType?.[inputContext] ?? column.inputType,
    [inputContext, column.contextInputType, column.inputType],
  );
  switch (resolvedInputType) {
    case "dropdown": {
      if (!column.optionsKey) {
        // This should not happen due to TS constraint on optionKey being required
        // whenever dropdown is used, but if we got here we have a bug
        console.error("Dropdown used without optionsKey");
        return null;
      }

      // ToDo: why this requires a custom dropdown?
      if (column.optionsKey === "deliveryStatus") {
        return <DeliveryStatusTag deliveryStatus={value as string} />;
      }

      const isProductDropdown =
        column.optionsKey === "products" || column.field === "product";
      const shouldDisableProductDropdown = !!(
        isProductDropdown &&
        (salesContract || purchaseContract)
      );
      // NOTE: we do this because for FK fields like tucks and trailers, the field is the label... not the ID, so
      // it's not always a valid choice, so it shows placeholder... really we should put value as the IE etc...
      const placeholder: string =
        typeof rowData[column.field as keyof TRow] === "string" &&
        rowData[column.field as keyof TRow] !== ""
          ? (rowData[column.field as keyof TRow] as string)
          : `Dobrać ${column.header}`;
      return (
        <Dropdown
          className={`w-full ${
            shouldDisableProductDropdown ? "hide-dropdown-trigger" : undefined
          }`}
          value={value}
          options={selectItems}
          optionLabel="label"
          optionValue="value"
          filter
          resetFilterOnHide={true}
          onFilter={(e) => {
            dropdownFilterRef.current = e.filter;
          }}
          showClear={!!value && !column.required}
          disabled={shouldDisableProductDropdown}
          placeholder={placeholder}
          onChange={(e) => onChange(e.value)}
          onHide={() => closeEditor?.()}
          panelFooterTemplate={
            typeof column.allowAddNew === "function" ? (
              <DropdownOpenAddNew<TRow>
                onAddNew={column.allowAddNew}
                row={rowData}
                filterValue={dropdownFilterRef}
              />
            ) : null
          }
          emptyFilterMessage="Nie znaleziono wyników"
          onKeyDown={(e) => e.stopPropagation()}
        />
      );
    }

    case "calendar":
      return (
        <Calendar
          value={isValidDate(value) ? value : null}
          showButtonBar
          onChange={(e) => {
            onChange(e.value); // ToDo: We forget that it is nullable here, which may cause trouble
            // ToDo: This is hacky and should be fixed to not rely on random timeouts
            setTimeout(() => {
              closeEditor?.();
            }, 100); // allow internal state to update first
          }}
          onTodayButtonClick={() => {
            onChange(new Date());
          }}
          dateFormat="d.mm.yy"
        />
      );

    case "number":
      return (
        <InputNumber
          value={value as number}
          mode="decimal"
          maxFractionDigits={2}
          useGrouping={true}
          // ToDo: We forget that it is nullable here, which may cause trouble
          onChange={(e) => {
            if (typeof e.value === "number") {
              onChange(e.value);
            }
          }}
          onKeyDown={(e) =>
            handleEditorEnterKey(
              e,
              (v) => {
                const normalized =
                  typeof v === "string" ? v.replace(",", ".") : v;
                onChange(Number(normalized));
              },
              closeEditor,
            )
          }
        />
      );

    case "chips":
      return (
        <CustomChipsDisplay
          field={column.field}
          rowData={rowData}
          value={value as string[]}
          // TS constraint only allows using chips with string values, so this conversion should be safe
          onChange={onChange as (updated: string[]) => void}
        />
      );

    case "toggle":
      // This is handled before entering the edit mode
      return "";

    case "text":
    default:
      return (
        <InputText
          className="w-full"
          value={value as string}
          // TS constraint only allows using text with string values, so this conversion should be safe
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) =>
            handleEditorEnterKey(
              e,
              onChange as (v: string) => void,
              closeEditor,
            )
          }
        />
      );
  }
}
